import { createTheme } from '@mui/material/styles';

// SmartAI Studio Color Palette
export const studioColors = {
  // Primary Colors
  coralRed: '#E16565',
  coralRedDark: '#D74A49',
  tealLight: '#4DD0B8',
  tealDark: '#2B9EB3',

  // Backgrounds
  darkBackground: '#2C2D2F',
  darkBackgroundPaper: '#3A3B3D',
  lightGrey: '#E5E5E5',
  lightBackground: '#F5F5F5',

  // Supporting Colors
  mutedTeal: '#8BA0A4',
  mutedTealDark: '#7A9A9F',
  lightCoral: '#F5A5A5',

  // Text Colors
  textPrimary: '#FFFFFF',
  textSecondary: '#E5E5E5',
  textDark: '#000000',
  textMuted: '#B0B0B0',
};

// Create SmartAI Studio dark theme
export const studioThemeDark = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: studioColors.coralRed,
      dark: studioColors.coralRedDark,
      light: studioColors.lightCoral,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: studioColors.tealDark,
      light: studioColors.tealLight,
      dark: studioColors.mutedTealDark,
      contrastText: '#FFFFFF',
    },
    background: {
      default: studioColors.darkBackground,
      paper: studioColors.darkBackgroundPaper,
    },
    text: {
      primary: studioColors.textPrimary,
      secondary: studioColors.textSecondary,
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    error: {
      main: '#FF6B6B',
    },
    warning: {
      main: '#FFD93D',
    },
    info: {
      main: studioColors.tealLight,
    },
    success: {
      main: '#51CF66',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: { fontWeight: 700, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.01em' },
    h3: { fontWeight: 600, letterSpacing: '-0.01em' },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    body1: { lineHeight: 1.7 },
    body2: { lineHeight: 1.6 },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: `${studioColors.mutedTeal} ${studioColors.darkBackgroundPaper}`,
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: studioColors.mutedTeal,
            border: `2px solid ${studioColors.darkBackgroundPaper}`,
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: studioColors.darkBackgroundPaper,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          padding: '10px 24px',
          transition: 'all 0.2s ease-in-out',
        },
        containedPrimary: {
          background: `linear-gradient(45deg, ${studioColors.coralRedDark} 30%, ${studioColors.coralRed} 90%)`,
          boxShadow: '0 3px 5px 2px rgba(225, 101, 101, .3)',
          '&:hover': {
            background: `linear-gradient(45deg, ${studioColors.coralRedDark} 60%, ${studioColors.coralRed} 100%)`,
            boxShadow: '0 4px 8px 3px rgba(225, 101, 101, .4)',
          },
        },
        containedSecondary: {
          background: `linear-gradient(45deg, ${studioColors.tealDark} 30%, ${studioColors.tealLight} 90%)`,
          boxShadow: '0 3px 5px 2px rgba(77, 208, 184, .3)',
          '&:hover': {
            background: `linear-gradient(45deg, ${studioColors.tealDark} 60%, ${studioColors.tealLight} 100%)`,
            boxShadow: '0 4px 8px 3px rgba(77, 208, 184, .4)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: studioColors.darkBackgroundPaper,
          backgroundImage: 'none',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundImage: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: studioColors.mutedTeal,
              borderWidth: 2,
            },
            '&:hover fieldset': {
              borderColor: studioColors.tealLight,
            },
            '&.Mui-focused fieldset': {
              borderColor: studioColors.tealLight,
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: studioColors.darkBackgroundPaper,
          backgroundImage: 'none',
          borderBottom: `1px solid ${studioColors.mutedTeal}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: studioColors.tealLight,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
  },
});

// Create SmartAI Studio light theme
export const studioThemeLight = createTheme({
  ...studioThemeDark,
  palette: {
    mode: 'light',
    primary: {
      main: studioColors.coralRed,
      dark: studioColors.coralRedDark,
      light: studioColors.lightCoral,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: studioColors.tealDark,
      light: studioColors.tealLight,
      dark: studioColors.mutedTealDark,
      contrastText: '#FFFFFF',
    },
    background: {
      default: studioColors.lightBackground,
      paper: '#FFFFFF',
    },
    text: {
      primary: studioColors.textDark,
      secondary: 'rgba(0, 0, 0, 0.7)',
    },
    divider: 'rgba(0, 0, 0, 0.12)',
  },
  components: {
    ...studioThemeDark.components,
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarColor: `${studioColors.mutedTeal} #F5F5F5`,
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            backgroundColor: studioColors.mutedTeal,
            border: '2px solid #F5F5F5',
          },
          '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
            backgroundColor: '#F5F5F5',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: studioColors.textDark,
          borderBottom: `1px solid ${studioColors.lightGrey}`,
        },
      },
    },
  },
});
